import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useBodyClass } from "@/hooks/useBodyClass";
import { useContent } from "@/hooks/useContent";
import { useProvideAlternateUrls } from "@/contexts/AlternateUrlsContext";
import { useLocalizedTexts, t } from "@/utils/translations";
import { stripLocaleFromPath } from "@/utils/locale-url";
import { B2B_USER_TYPE, type B2bLoginPageContent } from "@/types/b2bAccount";
import type { LayoutPayload } from "@/types/layout";
import { B2bLoginForm } from "./B2bLoginForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { ResetPasswordForm } from "./ResetPasswordForm";
import styles from "./B2bLoginPage.module.css";

interface B2bLoginPageProps {
  layoutData: LayoutPayload;
  /** Locale-prefixed path of the B2B my-account portal. */
  b2bMyAccountPath: string;
}

/**
 * B2B login page: username/password sign-in against the v2 b2bauth API, with a
 * forgot-password flow and a reset form (entered via ?token=&email= from the
 * reset email). Editor-controlled texts come from the B2bLoginPage content
 * node, with dictionary fallbacks when the content fetch fails.
 */
export function B2bLoginPage({
  layoutData,
  b2bMyAccountPath,
}: B2bLoginPageProps) {
  useBodyClass("page--light");
  const { pathname } = useLocation();
  // Best-effort fetch of the editor texts — errors fall back to dictionary keys.
  const { content } = useContent(stripLocaleFromPath(pathname));
  const texts = useLocalizedTexts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [forgotMode, setForgotMode] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Dedicated (non-CMS) route — supply per-culture URLs so the locale switcher
  // maps to the localized login page instead of falling back to home.
  useProvideAlternateUrls(layoutData.pages.b2bLoginPage?.alternateUrls);

  // Signed-in B2B members go straight to the portal. A signed-in B2C user
  // still sees the form (they may want to switch to their B2B account).
  if (layoutData.isUserAuthenticated && layoutData.userType === B2B_USER_TYPE) {
    window.location.assign(b2bMyAccountPath);
    return null;
  }

  const pageContent = (content ?? {}) as B2bLoginPageContent;
  const resetToken = searchParams.get("token");
  const resetEmail = searchParams.get("email");

  const usernameLabel =
    pageContent.username ||
    t(texts, "Tracker.MyAccountB2b.Username", "Username");
  const passwordLabel =
    pageContent.password || t(texts, "Tracker.Generic.Password", "Password");
  const fallbackError =
    pageContent.loginFailed ||
    t(
      texts,
      "Tracker.Generic.Error.WrongUsernameOrPassword",
      "Wrong username or password"
    );

  const handleResetSuccess = () => {
    // Drop the token/email params and show the login form with a confirmation.
    setSearchParams({}, { replace: true });
    setForgotMode(false);
    setNotice(
      t(
        texts,
        "Tracker.Generic.Notifications.ResetPasswordSuccessMessage",
        "Your password has been reset. You can now log in."
      )
    );
  };

  let heading: string;
  let body: React.ReactNode;
  if (resetToken && resetEmail) {
    heading = t(
      texts,
      "Tracker.Generic.ResetPasswordHeading",
      "Reset your password"
    );
    body = (
      <ResetPasswordForm
        email={resetEmail}
        token={resetToken}
        onSuccess={handleResetSuccess}
      />
    );
  } else if (forgotMode) {
    heading = t(
      texts,
      "Tracker.Generic.SendPasswordResetEmailHeading",
      "Forgot your password?"
    );
    body = (
      <ForgotPasswordForm
        usernameLabel={usernameLabel}
        onBack={() => setForgotMode(false)}
      />
    );
  } else {
    heading =
      pageContent.heading || t(texts, "Tracker.Generic.Login", "Log in");
    body = (
      <B2bLoginForm
        usernameLabel={usernameLabel}
        passwordLabel={passwordLabel}
        fallbackError={fallbackError}
        notice={notice}
        onForgotPassword={() => {
          setNotice(null);
          setForgotMode(true);
        }}
        onSuccess={() => window.location.assign(b2bMyAccountPath)}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.heading}>{heading}</h1>
        {body}
      </div>
    </div>
  );
}
